import { Injectable } from "@angular/core";
import { SAVE_QUOTE_CARD_TYPE } from "../interfaces/saved-quotes.input.interface";
import { SaveQuoteMetaData } from "../interfaces/save-quote-data.interface";
import { ContentStorageService } from "@ptl/global-components";
import { CONTENT_KEYS, PRODUCER_URL_PATH_CONTENT_KEYS } from "../interfaces/save-quote-email-referral.interface";

@Injectable({
  providedIn: 'root'
})
export class SaveQuoteReturnUrlService {

    producerUrlPaths: string[];

    constructor(
      private contentStorageService: ContentStorageService
    ){}
    /**
     * Because an AGENT can save an application.
     * Because AGENT-CONFIGURED pages / forms have AGENT-SPECIFIC URLs
     * Because we're triggering an AGENT-PROMPTED REFERRAL
     * 
     * We need to be able to send the consumer URLs which are specific to CONSUMERS
     * 
     * @param producerOrientedUrl The URL from a saved quote, which will convert a producer URL to the same path on the consumer side.
     */
    transformToConsumerReturnUrl(producerOrientedUrl: string): string { 
      
      const filteredMatches = this.producerUrlPaths.filter( v => producerOrientedUrl.includes(v));
  
      if (filteredMatches.length) {
          return producerOrientedUrl
            .replace(filteredMatches[0],'') // remove the producer path info
            .replaceAll('//','/'); // replace any duplicate slashes
      }
  
      return producerOrientedUrl;
    }

    /**
     * Temporarily preserve both old and new saved quote URL pattern.
     * Old Pattern: only captured return URL - will be retired once this has been live for more than a few months
     * New Pattern: captures a collection of URLs
     */
    getProducerReturnUrl(saveQuoteData: SaveQuoteMetaData): string {
      const newSaveQuoteURLPattern: SaveQuoteMetaData = saveQuoteData;
      const oldSaveQuoteURLPattern: { returnUrl: string } = saveQuoteData as unknown as { returnUrl: string };
  
      let url = '';
  
      if (newSaveQuoteURLPattern.urls) {
        url = newSaveQuoteURLPattern.urls.returnUrl;
      } else {
        url = oldSaveQuoteURLPattern.returnUrl
      }
  
      return url;
    }
    
    getConsumerReferralReturnUrl(saveQuoteData: SaveQuoteMetaData, quoteType: SAVE_QUOTE_CARD_TYPE, consumerDocumentUploadReturnUrl: string): string {    
      let url = '';
  
      switch (quoteType) {
        case SAVE_QUOTE_CARD_TYPE.DEFAULT_QUOTE_REFERRAL:
          // Scenario 1: An agent saved a quote, and is now resuming a quote
          // Scenario 2: An agent saved a quote, and is now sending a referral to a consumer, expectating them to land on the same page
          //   In scenario 2, elsewhere in this component, the URL will be rewritten from that of the agent-specific path to that of the consumer-specific path by stripping the agent-specific pathing:
          //   example: /individual/new-business/off-exchange/enroll/application/open-enrollment...
          //   will strip: /individual/new-business/off-exchange
          //   leaving: enroll/application/open-enrollment
          url = this.getProducerReturnUrl(saveQuoteData);
          break;
        case SAVE_QUOTE_CARD_TYPE.DOCUMENT_UPLOAD_REQUEST:
          // An agent saved a quote, and is now sending a referral to a consumer, expecting them to land on a different page, specifically, the document upload standalone in this case
          // saveForConsumerDocumentUploadPageUrl: comes from enrollment forms
          // consumerDocumentUploadReturnUrl: comes from authoring this page ( as in the case of saving from plan-details / compare-plans but still wanting the documents uploaded) - this is a super edge case
          url = saveQuoteData.urls.saveForConsumerDocumentUploadPageUrl || consumerDocumentUploadReturnUrl;
        default: break;
      }
  
      return url;
    }

    getContent() {
      // ["individual/new-business/off-exchange","individual/new-business/dental","individual/new-business/on-exchange"]
      this.contentStorageService.contentChanged$.subscribe( (content: Map<string, Map<string,string>>) => {
        if (content?.size > 0) {
          const saveQuoteCardContent = content.get(CONTENT_KEYS.PRODUCER_URL_PATH_CONTENT);
          if (saveQuoteCardContent) {
            const producerUrlPaths = saveQuoteCardContent.get(PRODUCER_URL_PATH_CONTENT_KEYS.URLS)
            if (producerUrlPaths) {
              try {
                this.producerUrlPaths = JSON.parse(producerUrlPaths);
              } catch (e) {
                console.error(e);
              }
            }
          }
        }
      })
    }
    
}